import {
  getCustomerHomeLegalEntity,
  getCustomerUuid,
} from '@camelot/webview-utils'
import { useLocalStorage } from '@vueuse/core'
import { defineStore, skipHydrate } from 'pinia'

export const useAppStore = defineStore('app', () => {
  // ---- State
  const accessToken = ref<string>('')
  const sessionId = ref<string>()

  // Use localStorage for state is possible, see https://pinia.vuejs.org/cookbook/composables.html#ssr
  const skipOnboarding = useLocalStorage('skipOnboarding', false)

  // ---- Session ID Management
  async function createAnonymousSessionId(): Promise<string> {
    const timestamp = Date.now()
    const randomValue = Math.random()
    const config = useRuntimeConfig()
    const sessionData = `${timestamp}-${randomValue}-${config.public.SENTRY_ENV || 'unknown'}`

    // Use crypto.subtle if available (both server and modern browsers)
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const msgBuffer = new TextEncoder().encode(sessionData)
      const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const hashHex = hashArray.map(b => (`00${b.toString(16)}`).slice(-2)).join('')
      return hashHex.substring(0, 36)
    }

    // fallback if not available
    return `session-${timestamp}-${Math.random().toString(36).substring(2, 15)}`
  }

  async function getSessionId(): Promise<string> {
    if (!sessionId.value) {
      sessionId.value = await createAnonymousSessionId()
    }
    return sessionId.value
  }

  function resetSession() {
    sessionId.value = undefined
  }

  // ---- Getters
  const customerUuid = computed(() =>
    accessToken.value ? getCustomerUuid(accessToken.value) : undefined)

  const customerHomeLegalEntityId = computed(() =>
    accessToken.value ? getCustomerHomeLegalEntity(accessToken.value) : undefined)

  return {
    accessToken,
    customerUuid,
    customerHomeLegalEntityId,
    skipOnboarding: skipHydrate(skipOnboarding),
    sessionId: readonly(sessionId),
    getSessionId,
    resetSession,
  }
})
